import { Loader2 } from 'lucide-react';
import { ShadcnButton, ShadcnButtonProps } from '@shadcn-features/core';
import { ShadcnSuspense } from '@shadcn-features/suspense';
import { ShadcnActionItem, ShadcnActionItemExtra } from './ShadcnAction';

const ShadcnActionButton = <
    Extra extends Record<string, unknown> = Record<string, unknown>,
>({
    actionExtraItem,
    onAction,
    onBeforeAction,
    disabled,
    overrideButtonProps,
    ...props
}: Omit<ShadcnActionItem<string, Extra>, 'actionCode'> & {
    actionExtraItem?: ShadcnActionItemExtra<Extra>;
    disabled?: boolean;
    onAction?: (extra?: Extra) => Promise<unknown> | unknown;
    onBeforeAction?: () => Promise<unknown> | unknown;
    overrideButtonProps?: ShadcnButtonProps;
}) => {
    const { actionText, Component, render, buttonProps } = {
        ...props,
        ...actionExtraItem,
        buttonProps: {
            ...props.buttonProps,
            ...actionExtraItem?.buttonProps,
            ...overrideButtonProps,
        },
    };

    if (render) {
        return (
            <ShadcnSuspense
                spin={<Loader2 className='h-6 w-6 animate-spin text-primary' />}
                style={{ minHeight: 32, minWidth: 100 }}
            >
                {render({
                    actionText,
                    onAction,
                    onBeforeAction,
                    buttonProps,
                    disabled,
                })}
            </ShadcnSuspense>
        );
    }

    if (Component) {
        return (
            <ShadcnSuspense
                spin={<Loader2 className='h-6 w-6 animate-spin text-primary' />}
                style={{ minHeight: 32, minWidth: 100 }}
            >
                <Component
                    actionText={actionText}
                    buttonProps={buttonProps}
                    disabled={disabled}
                    onAction={onAction}
                    onBeforeAction={onBeforeAction}
                />
            </ShadcnSuspense>
        );
    }

    return (
        <ShadcnButton
            variant='default'
            {...buttonProps}
            disabled={disabled || buttonProps?.disabled}
            onClick={async (e) => {
                await onBeforeAction?.();
                await onAction?.();
                buttonProps?.onClick?.(e);
            }}
        >
            {actionText}
        </ShadcnButton>
    );
};

export default ShadcnActionButton;

